import React, { useEffect, useState } from 'react';
import { Button, Col, Flex, Form, Input, Row, Typography, Alert, Progress, Card, Statistic, Divider, Spin, Tag } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import { getAiApiKey, saveAiApiKey, getTokenInfo } from './redux/slice';

const { Title, Text, Paragraph } = Typography;

const fmt = (n) => (n ?? 0).toLocaleString('de-DE');

const PLAN_COLORS = { S: 'default', M: 'blue', L: 'purple', XL: 'gold' };

const AiSettings = () => {
    const [form] = Form.useForm();
    const dispatch = useDispatch();
    const auth = useSelector(state => state.auth);
    const profile = useSelector(state => state.profile);
    const [showInput, setShowInput] = useState(false);

    const organizerMode = !!auth.decodeToken?.organizer;

    useEffect(() => {
        if (auth.decodeToken?._id) {
            dispatch(getAiApiKey(auth.decodeToken._id));
        }
        if (organizerMode) {
            dispatch(getTokenInfo());
        }
    }, [auth.decodeToken]);

    const handleSave = (values) => {
        dispatch(saveAiApiKey({ userId: auth.decodeToken._id, apiKey: values.apiKey }));
        form.resetFields();
        setShowInput(false);
    };

    const { isSet, maskedKey } = profile.aiKeyInfo || {};
    const ti = profile.tokenInfo;

    const usedPct      = ti ? Math.min(100, Math.round((ti.used / ti.limit) * 100)) : 0;
    const progressColor = usedPct >= 90 ? '#cf1322' : usedPct >= 70 ? '#fa8c16' : '#52c41a';

    return (
        <Flex className='paddingY-3 paddingX-2 general-settings bg-white' vertical gap={24}>
            <Title level={4}>KI-Einstellungen</Title>

            {/* ── Monatliches Token-Kontingent ─────────────────────────── */}
            {organizerMode && (
                <Card
                    title={
                        <Flex align='center' gap={8}>
                            <Text strong>Monatliches Token-Kontingent</Text>
                            {ti && !ti.hasOwnKey && (
                                <Tag color={PLAN_COLORS[ti.plan] || 'default'}>Plan {ti.plan}</Tag>
                            )}
                        </Flex>
                    }
                    className='border-green'
                >
                    {ti === null || ti === undefined ? (
                        <Flex justify='center'><Spin /></Flex>
                    ) : ti === false ? (
                        <Alert type='warning' showIcon message='Token-Daten konnten nicht geladen werden.' />
                    ) : ti.hasOwnKey ? (
                        <Alert
                            type='info'
                            showIcon
                            message='Eigener API-Key aktiv'
                            description='Sie verwenden Ihren eigenen Anthropic API-Key. Das monatliche Kontingent wird nicht angewendet.'
                        />
                    ) : (
                        <Flex vertical gap={16}>
                            <Row gutter={[16, 16]}>
                                <Col xs={24} sm={8}>
                                    <Statistic
                                        title='Monatliches Limit'
                                        value={fmt(ti.limit)}
                                        suffix='Tokens'
                                        valueStyle={{ fontSize: 17 }}
                                    />
                                </Col>
                                <Col xs={24} sm={8}>
                                    <Statistic
                                        title='Diesen Monat verbraucht'
                                        value={fmt(ti.used)}
                                        suffix='Tokens'
                                        valueStyle={{ fontSize: 17, color: progressColor }}
                                    />
                                </Col>
                                <Col xs={24} sm={8}>
                                    <Statistic
                                        title='Verbleibend'
                                        value={fmt(ti.remaining)}
                                        suffix='Tokens'
                                        valueStyle={{ fontSize: 17, color: ti.remaining === 0 ? '#cf1322' : '#3f8600' }}
                                    />
                                </Col>
                            </Row>

                            <div>
                                <Flex justify='space-between' style={{ marginBottom: 4 }}>
                                    <Text type='secondary'>{fmt(ti.used)} von {fmt(ti.limit)} Tokens</Text>
                                    <Text type='secondary'>{usedPct} %</Text>
                                </Flex>
                                <Progress
                                    percent={usedPct}
                                    strokeColor={progressColor}
                                    status={usedPct >= 100 ? 'exception' : 'normal'}
                                />
                            </div>

                            {usedPct >= 100 && (
                                <Alert
                                    type='error'
                                    showIcon
                                    message='Monatliches Kontingent erschöpft'
                                    description='KI-Funktionen sind bis zum Monatsanfang gesperrt. Bitte kontaktieren Sie uns für ein Upgrade auf einen höheren Plan.'
                                />
                            )}
                            {usedPct >= 70 && usedPct < 100 && (
                                <Alert
                                    type='warning'
                                    showIcon
                                    message='Kontingent fast aufgebraucht'
                                    description={`Sie haben ${usedPct} % Ihres monatlichen Kontingents verbraucht. Bei Bedarf kontaktieren Sie uns für einen Plan-Wechsel.`}
                                />
                            )}

                            {/* Plan-Übersicht */}
                            {ti.plans && (
                                <div>
                                    <Text type='secondary' style={{ fontSize: 12 }}>Verfügbare Pläne:</Text>
                                    <Row gutter={[8, 8]} style={{ marginTop: 8 }}>
                                        {Object.entries(ti.plans).map(([key, p]) => (
                                            <Col key={key}>
                                                <Tag
                                                    color={ti.plan === key ? PLAN_COLORS[key] : 'default'}
                                                    style={{ padding: '2px 10px', opacity: ti.plan === key ? 1 : 0.5 }}
                                                >
                                                    {p.label} — {(p.monthlyLimit / 1_000_000).toFixed(0)} Mio.
                                                    {p.priceEur === 0 ? ' (kostenlos)' : ` / €${p.priceEur}`}
                                                </Tag>
                                            </Col>
                                        ))}
                                    </Row>
                                </div>
                            )}
                        </Flex>
                    )}
                </Card>
            )}

            {/* ── Eigener API-Key (optional) ────────────────────────────── */}
            <Divider orientation='left' style={{ marginBottom: 0 }}>
                <Text type='secondary' style={{ fontSize: 13 }}>Eigener Anthropic API-Key (optional)</Text>
            </Divider>

            <Paragraph type='secondary' style={{ maxWidth: 600, marginTop: 0 }}>
                Mit einem eigenen Anthropic API-Key werden KI-Funktionen direkt über Ihr Konto abgerechnet —
                das monatliche Kontingent entfällt. Der Key wird verschlüsselt gespeichert.
            </Paragraph>

            {isSet && !showInput && (
                <Alert
                    type='success'
                    showIcon
                    message='Eigener API-Key hinterlegt'
                    description={
                        <Flex vertical gap={8}>
                            <Text>Aktueller Key: <Text code>{maskedKey}</Text></Text>
                            <Button size='small' onClick={() => setShowInput(true)}>Key ändern</Button>
                        </Flex>
                    }
                />
            )}

            {!isSet && !showInput && (
                <Alert
                    type='info'
                    showIcon
                    message='Kein eigener API-Key hinterlegt'
                    description={
                        <Flex vertical gap={8}>
                            <Text>Es wird das monatliche Plan-Kontingent verwendet.</Text>
                            <Button size='small' onClick={() => setShowInput(true)}>Eigenen API-Key hinterlegen</Button>
                        </Flex>
                    }
                />
            )}

            {showInput && (
                <Form form={form} layout='vertical' onFinish={handleSave} style={{ maxWidth: 500 }}>
                    <Form.Item
                        name='apiKey'
                        label='Anthropic API-Key'
                        rules={[
                            { required: true, message: 'Bitte geben Sie den API-Key ein.' },
                            { pattern: /^sk-ant-/, message: 'Anthropic API-Keys beginnen mit sk-ant-' },
                        ]}
                    >
                        <Input.Password placeholder='sk-ant-api03-...' autoComplete='off' />
                    </Form.Item>
                    <Row gutter={8}>
                        <Col>
                            <Button type='primary' htmlType='submit' loading={profile.loading}>Speichern</Button>
                        </Col>
                        <Col>
                            <Button danger onClick={() => { form.resetFields(); setShowInput(false); }}>Abbrechen</Button>
                        </Col>
                        {isSet && (
                            <Col>
                                <Button onClick={() => { dispatch(saveAiApiKey({ userId: auth.decodeToken._id, apiKey: '' })); setShowInput(false); }}>
                                    Key entfernen
                                </Button>
                            </Col>
                        )}
                    </Row>
                </Form>
            )}
        </Flex>
    );
};

export default AiSettings;
